import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Alert,
  Autocomplete,
  Chip,
  IconButton,
  Tooltip,
  Collapse,
} from "@mui/material";
import {
  People,
  Login,
  QuestionAnswer,
  TrendingUp,
  CalendarToday,
  Refresh,
  Search,
  Clear,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from "@mui/icons-material";
import { Get } from "../../../services/apiServices";
import { networkUrls } from "../../../services/networkurls";
import moment from "moment";
import ReuseableSnackbar from "../../../components/Snackbar/Snackbar";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

interface QueryDetail {
  id: number;
  message: string;
  timestamp: string;
  session_id: string;
}

interface UserDetail {
  user_id: number;
  username: string;
  email: string;
  total_logins: number;
  total_queries: number;
  first_login: string;
  last_login: string;
  avg_queries_per_day: number;
  days_active: number;
  queries: QueryDetail[];
}

interface DailyBreakdown {
  date: string;
  unique_users: number;
  total_logins: number;
  total_queries: number;
}

interface Summary {
  total_unique_users: number;
  total_logins: number;
  total_queries: number;
  avg_queries_per_user: number;
  avg_logins_per_user: number;
}

interface Period {
  start_date: string;
  end_date: string;
  days: number;
}

interface UserActivityReportResponse {
  period: Period;
  filters: {
    username: string | null;
  };
  summary: Summary;
  daily_breakdown: DailyBreakdown[];
  users: UserDetail[];
}

interface UsernameOption {
  user_id: number;
  username: string;
  email: string;
}

const UserActivityReport = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reportData, setReportData] = useState<UserActivityReportResponse | null>(null);
  const [usernames, setUsernames] = useState<UsernameOption[]>([]);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  
  // Filter states
  const [startDate, setStartDate] = useState<string>(
    moment().subtract(29, "days").format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = useState<string>(moment().format("YYYY-MM-DD"));
  const [selectedUsername, setSelectedUsername] = useState<UsernameOption | null>(null);
  
  const [snackbarInfo, setSnackbarInfo] = useState({
    color: "",
    message: "",
  });
  const [isShowSnackbar, setIsShowSnackbar] = useState(false);

  useEffect(() => {
    fetchUsernames();
    fetchUserActivityReport();
  }, []);

  const fetchUsernames = async () => {
    try {
      const response = await Get(networkUrls.usernamesList, true);
      if (response && response.status === 200) {
        setUsernames(response.data.usernames || []);
      }
    } catch (err: any) {
      console.error("Failed to fetch usernames:", err);
    }
  };

  const fetchUserActivityReport = async (customStartDate?: string, customEndDate?: string, customUsername?: UsernameOption | null) => {
    setLoading(true);
    setError(null);
    try {
      const dateStart = customStartDate !== undefined ? customStartDate : startDate;
      const dateEnd = customEndDate !== undefined ? customEndDate : endDate;
      const username = customUsername !== undefined ? customUsername : selectedUsername;

      let url = `${networkUrls.userActivityReport}?start_date=${dateStart}&end_date=${dateEnd}`;
      if (username) {
        url += `&username=${encodeURIComponent(username.username)}`;
      }

      const response = await Get(url, true);
      if (response && response.status === 200) {
        setReportData(response.data);
      } else {
        const errorMsg = response?.data?.detail || "Failed to fetch user activity report";
        setError(errorMsg);
        setSnackbarInfo({
          color: "error",
          message: errorMsg,
        });
        setIsShowSnackbar(true);
      }
    } catch (err: any) {
      const errorMsg = err?.response?.data?.detail || "An error occurred while fetching the report";
      setError(errorMsg);
      setSnackbarInfo({
        color: "error",
        message: errorMsg,
      });
      setIsShowSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    fetchUserActivityReport();
  };

  const handleClearFilters = () => {
    const newStartDate = moment().subtract(29, "days").format("YYYY-MM-DD");
    const newEndDate = moment().format("YYYY-MM-DD");
    
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    setSelectedUsername(null);
    
    // Fetch with cleared values immediately
    fetchUserActivityReport(newStartDate, newEndDate, null);
  };

  const toggleRowExpansion = (userId: number) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(userId)) {
      newExpandedRows.delete(userId);
    } else {
      newExpandedRows.add(userId);
    }
    setExpandedRows(newExpandedRows);
  };

  const StatCard = ({
    title,
    value,
    icon,
    color,
    subtitle,
  }: {
    title: string;
    value: string | number;
    icon: React.ReactNode;
    color: string;
    subtitle?: string;
  }) => (
    <Card
      sx={{
        height: "100%",
        background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
        border: `1px solid ${color}30`,
        borderRadius: 2,
        transition: "transform 0.2s, box-shadow 0.2s",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: `0 8px 16px ${color}20`,
        },
      }}
    >
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" fontWeight="bold" color={color}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              backgroundColor: `${color}20`,
              borderRadius: "50%",
              p: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading && !reportData) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
        flexDirection="column"
        gap={2}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          Loading user activity report...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box mb={3}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          User Activity Report
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Track user engagement, logins, and query patterns over time
        </Typography>
      </Box>

      {/* Filters Section */}
      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="600" mb={2}>
            <CalendarToday sx={{ fontSize: 20, mr: 1, verticalAlign: "middle" }} />
            Filters
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Start Date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: endDate }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="End Date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: startDate, max: moment().format("YYYY-MM-DD") }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                options={usernames}
                getOptionLabel={(option) => `${option.username} (${option.email})`}
                value={selectedUsername}
                onChange={(_, newValue) => setSelectedUsername(newValue)}
                renderInput={(params) => (
                  <TextField {...params} label="Filter by Username" placeholder="Search username..." />
                )}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box display="flex" gap={1}>
                <Button
                  variant="contained"
                  startIcon={<Search />}
                  onClick={handleApplyFilters}
                  fullWidth
                  sx={{
                    backgroundColor: "#4D8FAC",
                    "&:hover": { backgroundColor: "#3d7a94" },
                  }}
                >
                  Apply
                </Button>
                <Tooltip title="Clear Filters">
                  <IconButton
                    onClick={handleClearFilters}
                    sx={{
                      border: "1px solid #ddd",
                      borderRadius: 1,
                    }}
                  >
                    <Clear />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && !reportData && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {reportData && (
        <>
          {/* Period Info */}
          <Box mb={3}>
            <Chip
              icon={<CalendarToday />}
              label={`${moment(startDate).format("DD/MM/YYYY")} - ${moment(
                endDate
              ).format("DD/MM/YYYY")} (${moment(endDate).diff(moment(startDate), 'days') + 1} days)`}
              color="primary"
              sx={{ mr: 1 }}
            />
            {selectedUsername && (
              <Chip
                label={`User: ${selectedUsername.username}`}
                onDelete={() => {
                  setSelectedUsername(null);
                }}
                color="secondary"
              />
            )}
          </Box>

          {/* Summary Statistics Cards */}
          <Grid container spacing={3} mb={4}>
            <Grid item xs={12} sm={6} md={2.4}>
              <StatCard
                title="Total Users"
                value={reportData.summary.total_unique_users}
                icon={<People sx={{ fontSize: 32, color: "#4D8FAC" }} />}
                color="#4D8FAC"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <StatCard
                title="Total Logins"
                value={reportData.summary.total_logins}
                icon={<Login sx={{ fontSize: 32, color: "#2e7d32" }} />}
                color="#2e7d32"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <StatCard
                title="Total Queries"
                value={reportData.summary.total_queries}
                icon={<QuestionAnswer sx={{ fontSize: 32, color: "#1976d2" }} />}
                color="#1976d2"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <StatCard
                title="Avg Queries/User"
                value={reportData.summary.avg_queries_per_user}
                icon={<TrendingUp sx={{ fontSize: 32, color: "#ed6c02" }} />}
                color="#ed6c02"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <StatCard
                title="Avg Logins/User"
                value={reportData.summary.avg_logins_per_user}
                icon={<TrendingUp sx={{ fontSize: 32, color: "#9c27b0" }} />}
                color="#9c27b0"
              />
            </Grid>
          </Grid>

          {/* Daily Activity Chart */}
          <Card sx={{ mb: 4, borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="600" mb={3}>
                Daily Activity Trend
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={reportData.daily_breakdown}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(date) => moment(date).format("MMM DD")}
                  />
                  <YAxis />
                  <RechartsTooltip
                    labelFormatter={(date) => moment(date).format("MMM DD, YYYY")}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="unique_users"
                    stroke="#4D8FAC"
                    name="Unique Users"
                    strokeWidth={2}
                  />
                  <Line
                    type="monotone"
                    dataKey="total_logins"
                    stroke="#2e7d32"
                    name="Total Logins"
                    strokeWidth={2}
                  />
                  <Line
                    type="monotone"
                    dataKey="total_queries"
                    stroke="#1976d2"
                    name="Total Queries"
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Top Users Chart */}
          <Card sx={{ mb: 4, borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="600" mb={3}>
                Top 10 Most Active Users
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={reportData.users.slice(0, 10)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="username" />
                  <YAxis />
                  <RechartsTooltip />
                  <Legend />
                  <Bar dataKey="total_queries" fill="#1976d2" name="Total Queries" />
                  <Bar dataKey="total_logins" fill="#2e7d32" name="Total Logins" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* User Details Table with Expandable Queries */}
          <Card sx={{ mb: 4, borderRadius: 2 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" fontWeight="600">
                  User Activity Details
                </Typography>
                <Tooltip title="Refresh Data">
                  <IconButton onClick={() => fetchUserActivityReport()} color="primary">
                    <Refresh />
                  </IconButton>
                </Tooltip>
              </Box>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                      <TableCell width="50px" />
                      <TableCell><strong>Username</strong></TableCell>
                      <TableCell><strong>Email</strong></TableCell>
                      <TableCell align="center"><strong>Total Logins</strong></TableCell>
                      <TableCell align="center"><strong>Total Queries</strong></TableCell>
                      <TableCell align="center"><strong>Days Active</strong></TableCell>
                      <TableCell><strong>First Login</strong></TableCell>
                      <TableCell><strong>Last Login</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reportData.users.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">
                            No user activity data available for the selected period
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      reportData.users.map((user) => (
                        <React.Fragment key={user.user_id}>
                          <TableRow hover>
                            <TableCell>
                              <IconButton
                                size="small"
                                onClick={() => toggleRowExpansion(user.user_id)}
                                disabled={user.queries.length === 0}
                              >
                                {expandedRows.has(user.user_id) ? (
                                  <KeyboardArrowUp />
                                ) : (
                                  <KeyboardArrowDown />
                                )}
                              </IconButton>
                            </TableCell>
                            <TableCell>{user.username}</TableCell>
                            <TableCell>{user.email}</TableCell>
                            <TableCell align="center">
                              <Chip label={user.total_logins} color="success" size="small" />
                            </TableCell>
                            <TableCell align="center">
                              <Chip label={user.total_queries} color="primary" size="small" />
                            </TableCell>
                            <TableCell align="center">{user.days_active}</TableCell>
                            <TableCell>
                              {moment(user.first_login).format("MMM DD, YYYY HH:mm")}
                            </TableCell>
                            <TableCell>
                              {moment(user.last_login).format("MMM DD, YYYY HH:mm")}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                              <Collapse in={expandedRows.has(user.user_id)} timeout="auto" unmountOnExit>
                                <Box sx={{ margin: 2 }}>
                                  <Typography variant="h6" gutterBottom component="div" color="#4D8FAC">
                                    User Queries ({user.queries.length})
                                  </Typography>
                                  {user.queries.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                      No queries found for this user
                                    </Typography>
                                  ) : (
                                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow sx={{ backgroundColor: "#f9f9f9" }}>
                                            <TableCell><strong>Query</strong></TableCell>
                                            <TableCell><strong>Timestamp</strong></TableCell>
                                            {/* <TableCell><strong>Session ID</strong></TableCell> */}
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {user.queries.map((query) => (
                                            <TableRow key={query.id} hover>
                                              <TableCell sx={{ maxWidth: 400 }}>{query.message}</TableCell>
                                              <TableCell>
                                                {moment(query.timestamp).format("MMM DD, YYYY HH:mm")}
                                              </TableCell>
                                              {/* <TableCell>
                                                <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                                                  {query.session_id.substring(0, 8)}...
                                                </Typography>
                                              </TableCell> */}
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </TableContainer>
                                  )}
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </>
      )}

      {isShowSnackbar && (
        <ReuseableSnackbar
          isShowSnackbar={isShowSnackbar}
          setIsShowSnackbar={setIsShowSnackbar}
          message={snackbarInfo.message}
          colour={snackbarInfo.color}
        />
      )}
    </Box>
  );
};

export default UserActivityReport;
